import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, Thumb, Field, Pill, Cover } from "@/components/Bits";
import CoverImageField from "@/components/CoverImageField";
import ProjectNav from "@/components/ProjectNav";
import ProductBoard from "@/components/ProductBoard";
import FilesPanel from "@/components/FilesPanel";
import NotesPanel from "@/components/NotesPanel";
import RoomBudgetBar from "@/components/RoomBudgetBar";

const TABS = ["Overview", "Selections", "Files", "Budget", "Notes"];
const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-lime";

export default function SpaceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const [space, setSpace] = useState(null);
  const [products, setProducts] = useState([]);
  const [presentations, setPresentations] = useState([]);
  const [tab, setTab] = useState("Overview");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get(`/spaces/${id}`);
      setSpace(data);
      setForm(data);
      const p = await api.get("/products", { params: { space_id: id } });
      setProducts(p.data);
      const pres = await api.get("/presentations", { params: { space_id: id } });
      setPresentations(pres.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!space) return <div className="py-24 text-center text-sm text-fog">Loading space…</div>;

  const approved = products.filter((p) => p.selection_status === "Approved").reduce((a, p) => a + (p.client_total || 0), 0);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put(`/spaces/${id}`, { ...form, budget: Number(form.budget) || 0 });
      setSpace(data);
      setEditing(false);
      toast.success("Space updated");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-10" data-testid="space-detail-page">
      <ProjectNav
        projectId={space.project_id}
        projectName={space.project_name || "Project"}
        activeSection="Selections"
        trail={[
          { label: "Spaces", to: `/projects/${space.project_id}?tab=Spaces` },
          { label: space.name },
        ]}
      />

      <div className="card-surface overflow-hidden">
        {space.cover_file_id || space.cover_image ? (
          <Cover project={space} testId="space-cover-image" className="aspect-[21/9] w-full" />
        ) : (
          <Thumb className="aspect-[21/9] w-full" rounded="rounded-none" />
        )}
        <div className="flex flex-col gap-4 p-7 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="overline mb-2">{space.space_type || "Space"}</div>
            <h1 data-testid="space-title" className="font-serif text-3xl tracking-tight text-ink sm:text-4xl">{space.name}</h1>
            {space.description && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-stone">{space.description}</p>}
          </div>
          {isStaff && (
            <div className="flex flex-wrap gap-3">
              <Link data-testid="space-add-product-btn" to={`/products/new?project=${space.project_id}&space=${id}`} className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
                <Plus className="h-4 w-4" strokeWidth={1.5} /> Add Product
              </Link>
              <button data-testid="space-edit-btn" onClick={() => setEditing((s) => !s)} className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink hover:bg-line/50">
                <Pencil className="h-4 w-4" strokeWidth={1.5} /> Edit
              </button>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <form onSubmit={save} className="card-surface grid gap-5 p-7 sm:grid-cols-2" data-testid="space-edit-form">
          <Field label="Space Name"><input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} /></Field>
          <Field label="Space Type"><input value={form.space_type || ""} onChange={(e) => setForm({ ...form, space_type: e.target.value })} className={input} /></Field>
          <Field label="Budget"><input data-testid="space-edit-budget" type="number" value={form.budget || 0} onChange={(e) => setForm({ ...form, budget: e.target.value })} className={`num ${input}`} /></Field>
          <Field label="Rendering URL"><input data-testid="space-rendering-input" value={form.rendering_image || ""} onChange={(e) => setForm({ ...form, rendering_image: e.target.value })} className={input} /></Field>
          <div className="sm:col-span-2">
            <CoverImageField
              projectId={space.project_id}
              value={form}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            />
          </div>
          <label className="flex items-center gap-3 text-sm text-ink sm:col-span-2">
            <input data-testid="space-financials-toggle" type="checkbox" checked={!!form.financials_visible_to_client} onChange={(e) => setForm({ ...form, financials_visible_to_client: e.target.checked })} className="h-4 w-4 accent-[#BEC221]" />
            Show space budget and approved investment to the client
          </label>
          <Field label="Budget Impact During Approvals" className="sm:col-span-2">
            <select
              data-testid="space-budget-impact-select"
              value={form.show_budget_impact === true ? "on" : form.show_budget_impact === false ? "off" : "inherit"}
              onChange={(e) =>
                setForm({
                  ...form,
                  show_budget_impact: e.target.value === "inherit" ? null : e.target.value === "on",
                })
              }
              className={input}
            >
              <option value="inherit">Follow the project setting</option>
              <option value="on">Always show for this room</option>
              <option value="off">Never show for this room</option>
            </select>
          </Field>
          <Field label="Description" className="sm:col-span-2"><textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className={input} /></Field>
          <div className="sm:col-span-2">
            <button data-testid="space-save-btn" type="submit" className="rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Save Space</button>
          </div>
        </form>
      )}

      <div className="flex gap-7 overflow-x-auto border-b border-line scroll-thin" data-testid="space-tabs">
        {TABS.filter((t) => isStaff || ["Overview", "Selections", "Files"].includes(t)).map((t) => (
          <button
            key={t}
            data-testid={`space-tab-${t.toLowerCase()}`}
            onClick={() => setTab(t)}
            className={`whitespace-nowrap border-b-2 pb-3 text-sm transition-colors ${tab === t ? "border-lime font-medium text-ink" : "border-transparent text-stone hover:text-ink"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="space-y-10">
          {(() => {
            const current = presentations.find((p) => p.status === "Published") || presentations[0];
            return current ? (
              <div className="card-surface flex flex-col gap-6 overflow-hidden sm:flex-row" data-testid="space-current-presentation">
                {current.hero_image && <img src={current.hero_image} alt="" className="h-40 w-full object-cover sm:h-auto sm:w-56" />}
                <div className="flex flex-1 flex-col justify-between gap-4 p-6">
                  <div>
                    <div className="overline mb-2">Current Presentation</div>
                    <h3 className="font-serif text-2xl text-ink">{current.title || current.name}</h3>
                    {current.narrative && <p className="mt-2 line-clamp-2 text-sm text-stone">{current.narrative}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Link data-testid="view-presentation-btn" to={`/presentations/${current.id}`} className="rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
                      View Design Presentation
                    </Link>
                    {isStaff && <Pill tone="bg-line text-stone">{current.status}</Pill>}
                  </div>
                </div>
              </div>
            ) : null;
          })()}

          <div className="card-surface grid grid-cols-2 gap-6 p-7 sm:grid-cols-4" data-testid="space-progress">
            {[
              ["Selections Approved", products.filter((p) => p.selection_status === "Approved").length],
              ["Awaiting Approval", products.filter((p) => p.selection_status === "Awaiting Approval").length],
              ["Items Ordered", products.filter((p) => ["Ordered", "Order Processing", "Preparing to Ship", "On the Way"].includes(p.client_status)).length],
              ["Items Delivered", products.filter((p) => ["Delivered", "Installed"].includes(p.client_status)).length],
            ].map(([l, v]) => (
              <div key={l}>
                <div className="num text-2xl text-ink">{v}</div>
                <div className="overline mt-1 leading-tight">{l}</div>
              </div>
            ))}
          </div>

          {space.show_financials && space.budget != null && (
            <div className="card-surface p-7" data-testid="space-investment">
              <div className="overline mb-4">{isStaff ? "Room Budget Health" : "Your Room Budget"}</div>
              <RoomBudgetBar
                testId="space-budget-bar"
                budget={space.budget}
                approved={space.approved_total ?? approved}
                pending={space.pending_total}
              />
            </div>
          )}
          <div>
            <SectionTitle>Product Board</SectionTitle>
            <ProductBoard products={products} addLink={isStaff ? `/products/new?project=${space.project_id}&space=${id}` : null} emptyTitle={`No selections in ${space.name}`} />
          </div>
          {!isStaff && (
            <div>
              <SectionTitle>Files</SectionTitle>
              <FilesPanel projectId={space.project_id} spaceId={id} />
            </div>
          )}
        </div>
      )}

      {tab === "Selections" && <ProductBoard products={products} addLink={isStaff ? `/products/new?project=${space.project_id}&space=${id}` : null} emptyTitle={isStaff ? `No selections in ${space.name}` : `${space.name} selections are coming soon`} />}

      {tab === "Files" && <FilesPanel projectId={space.project_id} spaceId={id} />}

      {tab === "Budget" && isStaff && (
        <div className="card-surface overflow-x-auto scroll-thin">
          <table className="w-full min-w-[600px] text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                {["Product", "Qty", "Client Total", "Designer Cost", "Status"].map((h) => <th key={h} className="overline px-5 py-3">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-line last:border-0 hover:bg-bone">
                  <td className="px-5 py-3"><Link to={`/products/${p.id}`} className="text-ink hover:text-lime-dark">{p.name}</Link></td>
                  <td className="num px-5 py-3 text-right">{p.quantity}</td>
                  <td className="num px-5 py-3 text-right">{money(p.client_total)}</td>
                  <td className="num px-5 py-3 text-right text-stone">{money(p.designer_total_cost)}</td>
                  <td className="px-5 py-3 text-stone">{p.selection_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "Notes" && isStaff && (
        <div className="card-surface p-7">
          <NotesPanel entityType="space" entityId={id} projectId={space.project_id} title="Space Internal Notes" />
        </div>
      )}
    </div>
  );
}
